import { useQuery } from '@tanstack/react-query';
import axiosClient from 'api/config/axios';
import { adaptPayoutChannel } from 'api/escrow/adapters';
import { PayoutChannel } from 'api/escrow/types';
import { memoryQueryClient } from 'providers/TanstackQuery';

/**
 * Fetches payout channels for a given account type slug and optional currency.
 *
 * When `currency` is provided it is appended to the query string so the backend
 * filters out mixed-currency duplicates of the same channel code.
 */
async function getPayoutChannels(slug: string, currency?: string): Promise<PayoutChannel[]> {
  let url = `/api/v1/escrow/payout-channels?account_type=${encodeURIComponent(slug)}`;
  if (currency) {
    url += `&currency=${encodeURIComponent(currency)}`;
  }
  const result = await axiosClient.query.get(url);
  return (result.data.channels ?? []).map(adaptPayoutChannel);
}

export const getPayoutChannelsQueryKey = (slug: string | undefined, currency?: string) =>
  ['escrow', 'payout-channels', slug, currency] as const;

/**
 * React Query hook that fetches payout channels for a given account type slug.
 *
 * When `currency` is supplied the query key includes it, ensuring that switching
 * currency within the same session fetches fresh data instead of serving stale cache.
 */
export function useGetPayoutChannelsQuery(
  slug: string | undefined,
  options?: { currency?: string; enabled?: boolean },
) {
  const currency = options?.currency;
  return useQuery(
    {
      queryKey: getPayoutChannelsQueryKey(slug, currency),
      queryFn: () => getPayoutChannels(slug!, currency),
      enabled: !!slug && (options?.enabled ?? true),
    },
    memoryQueryClient,
  );
}
